import React, { useReducer } from 'react';
import { Stack } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import Api from '../API';
import GenericTable from '../../Components/GenericTable/GenericTable';
import { CandidatePoolsReducer, InitialCandidatePoolsState } from '../reducer';
import { ICandidateSourcingPool, ICandidatePoolsApiResponse } from '../types';
import CandidatePoolTableRows from '../CandidatePoolTableRows';
import { headerStyle } from '../../Candidates/config';
import { PlaylistAdd } from '@mui/icons-material';
import { candidatePoolsActionsList } from '../config';
import CandidatePoolsTableActionComponents from '../CandidatePoolsTableActionComponents';
import SearchBar from '../SearchBar';
import AddButton from '../../Components/GenericTable/AddButton';

const CandidatePools = () => {
  const [candidatePoolsState, dispatch] = useReducer(
    CandidatePoolsReducer,
    InitialCandidatePoolsState
  );
  const {
    candidatePoolsTableState,
    candidatePoolsTableSettings,
    candidatePoolsCount,
    totalPages,
    searchQuery
  } = candidatePoolsState;

  const {
    data: candidatePoolsData,
    isLoading: isFetchingCandidatePools,
    refetch: refetchCandidatePools
  } = useQuery({
    queryKey: [
      'candidatePools',
      candidatePoolsTableSettings.page,
      candidatePoolsTableSettings.limit,
      candidatePoolsTableSettings.sort,
      candidatePoolsTableSettings.sort_order,
      searchQuery
    ],
    queryFn: async () => {
      const response = await Api.getCandidatePools(
        candidatePoolsTableSettings.page,
        candidatePoolsTableSettings.limit,
        candidatePoolsTableSettings.sort,
        candidatePoolsTableSettings.sort_order,
        searchQuery
      );
      const responseData = response as ICandidatePoolsApiResponse;

      if (responseData.resHead) {
        dispatch({
          type: 'SET_CANDIDATE_POOLS_COUNT',
          payload: parseInt(responseData.resHead['x-total-count'] || '0')
        });
        dispatch({
          type: 'SET_TOTAL_PAGES',
          payload: parseInt(responseData.resHead['x-total-pages'] || '0')
        });
      }

      return responseData;
    }
  });

  const handleCreateNewPool = () => {
    // TODO: Implement create new pool functionality
    console.log('Create new pool clicked');
  };

  const handleSearchChange = (query: string) => {
    dispatch({ type: 'SET_SEARCH_QUERY', payload: query });
  };

  const handleSearch = () => {
    refetchCandidatePools();
  };

  const handleClearSearch = () => {
    dispatch({ type: 'CLEAR_SEARCH_QUERY' });
  };

  const candidatePools = candidatePoolsData?.res?.pools || [];

  return (
    <Stack sx={{ padding: '20px' }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ marginBottom: '20px' }}
      >
        <Stack direction="row" spacing={2} sx={{ flex: 1, marginRight: '20px' }}>
          <SearchBar
            onSearchChange={handleSearchChange}
            onSearch={handleSearch}
            onClear={handleClearSearch}
            placeholder="Search candidate pools..."
          />
        </Stack>
        <AddButton title="Create New Pool" onClick={handleCreateNewPool}>
          <PlaylistAdd />
        </AddButton>
      </Stack>

      <GenericTable
        label="candidate pools list"
        items={candidatePools as ICandidateSourcingPool[]}
        isLoadingItems={isFetchingCandidatePools}
        tableState={candidatePoolsTableState}
        dispatch={dispatch}
        headerStyle={headerStyle}
        paginationState={candidatePoolsTableSettings}
        pagination={{
          totalItems: candidatePoolsCount,
          totalPages
        }}
        TableRows={(props) => <CandidatePoolTableRows {...props} dispatch={dispatch} />}
        TableActionList={candidatePoolsActionsList}
        allowMultipleSelection={candidatePoolsCount > 0}
        titleCellId="id"
        userPreferenceSource="candidatePools"
      />

      <CandidatePoolsTableActionComponents
        actionsState={candidatePoolsTableState.actions}
        dispatch={dispatch}
        refetchCandidatePools={refetchCandidatePools}
      />
    </Stack>
  );
};

export default CandidatePools;
